import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useCelebration } from '@/context/CelebrationContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { User, Bell, Shield, MapPin, LogOut, Users, Diamond, Gift, Instagram, Linkedin, Twitter, Globe, Sparkles, Check, Pencil, CreditCard, AlertTriangle, Crown, X, Heart, Mail, Send, Camera, Loader2, Trash2, Award, Calendar, MessageSquare, Phone } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const { user, logout, updateLocation, refreshUser } = useAuth();
  const { grandCelebrate, quickCelebrate } = useCelebration();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notifications, setNotifications] = useState({
    messages: true,
    communityUpdates: true,
    sessionReminders: true,
    marketing: false
  });
  const [locationEnabled, setLocationEnabled] = useState(!!user?.location);
  const [digestPreview, setDigestPreview] = useState(null);
  const [loadingDigest, setLoadingDigest] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  
  // Notification delivery preferences
  const [notificationPrefs, setNotificationPrefs] = useState({
    delivery_method: 'email',
    phone_number: '',
    sms_available: false,
    categories: {
      session_reminder: true,
      new_message: true,
      community_activity: true,
      journey_milestone: true,
      general: true
    }
  });
  const [loadingNotificationPrefs, setLoadingNotificationPrefs] = useState(false);
  const [savingNotificationPrefs, setSavingNotificationPrefs] = useState(false);
  const [sendingTestSms, setSendingTestSms] = useState(false);
  
  // Reflection prompts state
  const [reflectionPrompts, setReflectionPrompts] = useState([]);
  const [editingReflection, setEditingReflection] = useState(null);
  const [reflectionText, setReflectionText] = useState('');
  const [savingReflection, setSavingReflection] = useState(false);

  // Membership cancellation state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showCancelSuccessDialog, setShowCancelSuccessDialog] = useState(false);
  const [cancellingMembership, setCancellingMembership] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancellationResult, setCancellationResult] = useState(null);

  // Profile photo upload state
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [profileImage, setProfileImage] = useState(null);

  // Profile form state
  const [profileData, setProfileData] = useState({
    city: '',
    state: '',
    bio: '',
    directory_visible: true,
    social_links: {
      instagram: '',
      linkedin: '',
      twitter: '',
      website: ''
    }
  });

  // Journey status state
  const [journeyStatus, setJourneyStatus] = useState(null);
  const [loadingJourney, setLoadingJourney] = useState(false);

  // Initialize profile image from user data
  useEffect(() => {
    if (user?.profile_image) {
      // Build full URL if it's a relative path
      const imageUrl = user.profile_image.startsWith('http') 
        ? user.profile_image 
        : `${API_URL}${user.profile_image}`;
      setProfileImage(imageUrl);
    }
  }, [user]);

  useEffect(() => {
    fetchSettings();
    fetchReflectionPrompts();
    fetchJourneyStatus();
  }, []);

  const fetchJourneyStatus = async () => {
    setLoadingJourney(true);
    try {
      const response = await axios.get(`${API_URL}/api/my/journey-status`);
      setJourneyStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch journey status:', error);
    } finally {
      setLoadingJourney(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user/settings`);
      setSettings(response.data);
      setProfileData({
        city: response.data.city || '',
        state: response.data.state || '',
        bio: response.data.bio || '',
        directory_visible: response.data.directory_visible !== false,
        social_links: response.data.social_links || {
          instagram: '',
          linkedin: '',
          twitter: '',
          website: ''
        }
      });
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReflectionPrompts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user/reflection-prompts`);
      setReflectionPrompts(response.data.prompts || []);
    } catch (error) {
      console.error('Failed to fetch reflection prompts:', error);
    }
  };

  const fetchNotificationPrefs = async () => {
    setLoadingNotificationPrefs(true);
    try {
      const response = await axios.get(`${API_URL}/api/user/notification-preferences`);
      setNotificationPrefs({
        delivery_method: response.data.delivery_method || 'email',
        phone_number: response.data.phone_number || '',
        phone_verified: response.data.phone_verified || false,
        sms_available: response.data.sms_available || false,
        categories: response.data.categories || {
          session_reminder: true,
          new_message: true,
          community_activity: true,
          journey_milestone: true,
          general: true
        }
      });
    } catch (error) {
      console.error('Failed to fetch notification preferences:', error);
    } finally {
      setLoadingNotificationPrefs(false);
    }
  };

  const saveNotificationPrefs = async () => {
    setSavingNotificationPrefs(true);
    try {
      await axios.put(`${API_URL}/api/user/notification-preferences`, {
        delivery_method: notificationPrefs.delivery_method,
        phone_number: notificationPrefs.phone_number || null,
        categories: notificationPrefs.categories
      });
      toast.success('Notification preferences updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update preferences');
    } finally {
      setSavingNotificationPrefs(false);
    }
  };

  const sendTestSms = async () => {
    setSendingTestSms(true);
    try {
      const response = await axios.post(`${API_URL}/api/user/send-test-sms`);
      toast.success(response.data.message || 'Test SMS sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test SMS');
    } finally {
      setSendingTestSms(false);
    }
  };

  // Load notification preferences on mount
  useEffect(() => {
    fetchNotificationPrefs();
  }, []);

  const handleSaveReflection = async (promptId) => {
    if (!reflectionText.trim()) {
      toast.error('Please enter a response');
      return;
    }
    
    setSavingReflection(true);
    try {
      await axios.post(`${API_URL}/api/user/reflection`, {
        prompt_id: promptId,
        response: reflectionText
      });
      
      // Update local state
      setReflectionPrompts(prev => 
        prev.map(p => p.id === promptId ? { ...p, response: reflectionText } : p)
      );
      
      // Check if this completes all reflections
      const completedCount = reflectionPrompts.filter(p => p.response || p.id === promptId).length;
      if (completedCount === reflectionPrompts.length) {
        grandCelebrate('sparkle', 'profileComplete');
      } else {
        quickCelebrate('Reflection saved! +5 diamonds added to your joy bank');
      }
      
      setEditingReflection(null);
      setReflectionText('');
      toast.success('Reflection saved! +5 diamonds added to your joy bank');
    } catch (error) {
      toast.error('Failed to save reflection');
    } finally {
      setSavingReflection(false);
    }
  };

  const startEditingReflection = (prompt) => {
    setEditingReflection(prompt.id);
    setReflectionText(prompt.response || '');
  };

  // Handle profile photo upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPG, PNG, WebP, or GIF)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/auth/profile-photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Build full URL with cache-busting timestamp
      const fullImageUrl = `${API_URL}${response.data.url}?t=${Date.now()}`;
      setProfileImage(fullImageUrl);
      
      // Refresh user data to update everywhere
      await refreshUser();
      
      toast.success('Profile photo updated!');
      
      // Trigger a celebration for first photo upload
      if (!user?.profile_image) {
        quickCelebrate('Beautiful! Your photo is now live');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Handle profile photo removal
  const handleRemovePhoto = async () => {
    setUploadingPhoto(true);
    try {
      await axios.delete(`${API_URL}/api/auth/profile-photo`);
      setProfileImage(null);
      await refreshUser();
      toast.success('Profile photo removed');
    } catch (error) {
      toast.error('Failed to remove photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/user/settings`, profileData);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleLocation = async () => {
    if (!locationEnabled) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            await updateLocation(position.coords.latitude, position.coords.longitude);
            setLocationEnabled(true);
            toast.success('Location enabled');
          },
          () => {
            toast.error('Could not get location');
          }
        );
      }
    } else {
      setLocationEnabled(false);
      toast.success('Location disabled');
    }
  };

  const handleCancelMembership = async () => {
    setCancellingMembership(true);
    try {
      const response = await axios.post(`${API_URL}/api/membership/cancel`, {
        tier: 'inner_circle',
        reason: cancelReason
      });
      
      // Calculate days remaining
      const effectiveDate = response.data.effective_date;
      let daysRemaining = 30; // Default
      if (effectiveDate) {
        const endDate = new Date(effectiveDate);
        const today = new Date();
        daysRemaining = Math.max(0, Math.ceil((endDate - today) / (1000 * 60 * 60 * 24)));
      }
      
      setCancellationResult({
        daysRemaining,
        effectiveDate
      });
      setShowCancelDialog(false);
      setShowCancelSuccessDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel membership. Please try again.');
    } finally {
      setCancellingMembership(false);
    }
  };

  const handleReactivateMembership = () => {
    // Close the success dialog and show a message
    setShowCancelSuccessDialog(false);
    toast.success('Welcome back, Sis! Your membership remains active.', {
      description: "We're so glad you decided to stay with us."
    });
    window.location.reload();
  };

  // Check if user has Inner Circle membership
  const hasInnerCircle = user?.active_tiers?.includes('inner_circle');
  const hasSDC = user?.active_tiers?.includes('sdc') || user?.active_tiers?.includes('sdc_lounge');
  const hasInnerEdge = user?.active_tiers?.includes('inner_edge');
  const hasRetreat = user?.active_tiers?.includes('costa_rica_retreat');

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-3xl" data-testid="settings-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl lg:text-4xl font-bold text-forest">
            Your Sanctuary
          </h1>
          <p className="text-gray-600 mt-2">
            Manage your sacred space and preferences.
          </p>
        </div>

        {/* Profile Section */}
        <Card className="card-base mb-6">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <User className="h-5 w-5 text-forest" />
              Your Story
            </CardTitle>
            <CardDescription>Let us see the real you</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Profile Photo Upload Section */}
            <div className="flex items-start gap-6">
              <div className="relative group">
                <div className="h-24 w-24 rounded-full border-4 border-forest/10 overflow-hidden bg-forest flex items-center justify-center">
                  {profileImage ? (
                    <img 
                      key={profileImage}
                      src={profileImage}
                      alt="Profile"
                      className="h-full w-full object-cover"
                    />
                  ) : user?.profile_image ? (
                    <img 
                      key={user.profile_image}
                      src={`${API_URL}${user.profile_image}`}
                      alt="Profile"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-white text-3xl font-semibold">
                      {user?.full_name?.charAt(0)?.toUpperCase()}
                    </span>
                  )}
                </div>
                
                {/* Upload Overlay */}
                <label 
                  className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity"
                  htmlFor="profile-photo-input"
                >
                  {uploadingPhoto ? (
                    <Loader2 className="h-6 w-6 text-white animate-spin" />
                  ) : (
                    <Camera className="h-6 w-6 text-white" />
                  )}
                </label>
                <input
                  id="profile-photo-input"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={handlePhotoUpload}
                  disabled={uploadingPhoto}
                />
              </div>
              
              <div className="flex-1">
                <h3 className="font-heading text-xl font-semibold text-forest">{user?.full_name}</h3>
                <p className="text-gray-500 mb-3">{user?.email}</p>
                
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-forest border-forest hover:bg-forest hover:text-white"
                    onClick={() => document.getElementById('profile-photo-input').click()}
                    disabled={uploadingPhoto}
                  >
                    {uploadingPhoto ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                    ) : (
                      <><Camera className="h-4 w-4 mr-2" /> {profileImage ? 'Change Photo' : 'Upload Photo'}</>
                    )}
                  </Button>
                  
                  {profileImage && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={handleRemovePhoto}
                      disabled={uploadingPhoto}
                    >
                      <Trash2 className="h-4 w-4 mr-2" /> Remove
                    </Button>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-2">JPG, PNG, WebP or GIF. Max 5MB.</p>
              </div>
            </div>

            <Separator />

            <div className="grid gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input 
                    id="city" 
                    value={profileData.city}
                    onChange={(e) => setProfileData({ ...profileData, city: e.target.value })}
                    placeholder="Your city"
                    className="input-base mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input 
                    id="state" 
                    value={profileData.state}
                    onChange={(e) => setProfileData({ ...profileData, state: e.target.value })}
                    placeholder="Your state"
                    className="input-base mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="bio">Bio</Label>
                <Textarea 
                  id="bio" 
                  value={profileData.bio}
                  onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                  placeholder="Tell other members about yourself..."
                  className="input-base mt-1"
                />
              </div>
            </div>

            <Separator />

            <div>
              <h4 className="font-medium text-forest mb-3">Social Media Links</h4>
              <div className="grid gap-3">
                <div className="flex items-center gap-3">
                  <Instagram className="h-5 w-5 text-pink-500" />
                  <Input 
                    value={profileData.social_links.instagram}
                    onChange={(e) => setProfileData({ 
                      ...profileData, 
                      social_links: { ...profileData.social_links, instagram: e.target.value }
                    })}
                    placeholder="Instagram URL"
                    className="input-base flex-1"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Linkedin className="h-5 w-5 text-blue-600" />
                  <Input 
                    value={profileData.social_links.linkedin}
                    onChange={(e) => setProfileData({ 
                      ...profileData, 
                      social_links: { ...profileData.social_links, linkedin: e.target.value }
                    })}
                    placeholder="LinkedIn URL"
                    className="input-base flex-1"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Twitter className="h-5 w-5 text-blue-400" />
                  <Input 
                    value={profileData.social_links.twitter}
                    onChange={(e) => setProfileData({ 
                      ...profileData, 
                      social_links: { ...profileData.social_links, twitter: e.target.value }
                    })}
                    placeholder="Twitter/X URL"
                    className="input-base flex-1"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="h-5 w-5 text-forest" />
                  <Input 
                    value={profileData.social_links.website}
                    onChange={(e) => setProfileData({ 
                      ...profileData, 
                      social_links: { ...profileData.social_links, website: e.target.value }
                    })}
                    placeholder="Website URL"
                    className="input-base flex-1"
                  />
                </div>
              </div>
            </div>

            <Button className="btn-primary" onClick={handleSaveProfile} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </CardContent>
        </Card>

        {/* Member Directory Section */}
        <Card className="card-base mb-6">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Users className="h-5 w-5 text-forest" />
              Sister Directory
            </CardTitle>
            <CardDescription>Control your visibility in the sisterhood</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-forest">Let Sisters Find You</p>
                <p className="text-sm text-gray-500">Other sisters can connect with you</p>
              </div>
              <Switch 
                checked={profileData.directory_visible}
                onCheckedChange={(checked) => {
                  setProfileData({ ...profileData, directory_visible: checked });
                  // Auto-save this setting
                  axios.put(`${API_URL}/api/user/settings`, { directory_visible: checked })
                    .then(() => toast.success(checked ? 'Now visible in directory' : 'Hidden from directory'))
                    .catch(() => toast.error('Failed to update'));
                }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Diamond Points Section - Enhanced with Redemption Categories */}
        <Card className="card-base mb-6" data-testid="diamond-points-section">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Diamond className="h-5 w-5 text-purple-600" />
              Your Joy Bank
            </CardTitle>
            <CardDescription>Rewards for showing up for yourself</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-3xl font-bold text-purple-600">{settings?.diamond_points || 0}</p>
                <p className="text-sm text-gray-500">Total diamonds</p>
                <p className="text-xs text-purple-600 font-medium mt-1">
                  = ${((settings?.diamond_points || 0) / 20).toFixed(2)} value
                </p>
              </div>
              <div className="h-16 w-16 rounded-full bg-purple-100 flex items-center justify-center">
                <Diamond className="h-8 w-8 text-purple-600" />
              </div>
            </div>
            
            {/* Redemption Categories */}
            <div className="space-y-3 mb-4">
              <p className="text-sm font-medium text-forest">Redeem Your Diamonds:</p>
              
              {/* Swag */}
              <div className={`p-3 rounded-lg border ${(settings?.diamond_points || 0) >= 100 ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">👕</span>
                    <div>
                      <p className="font-medium text-sm text-forest">YUMMY Marketplace & Swag</p>
                      <p className="text-xs text-gray-500">Apparel, accessories, self-care</p>
                    </div>
                  </div>
                  <Badge variant="outline" className={`text-xs ${(settings?.diamond_points || 0) >= 100 ? 'border-green-500 text-green-600' : ''}`}>
                    Min: 100 pts ($5)
                  </Badge>
                </div>
              </div>
              
              {/* Coaching */}
              <div className={`p-3 rounded-lg border ${(settings?.diamond_points || 0) >= 200 ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">💎</span>
                    <div>
                      <p className="font-medium text-sm text-forest">Signature Coaching</p>
                      <p className="text-xs text-gray-500">Apply toward your coaching investment</p>
                    </div>
                  </div>
                  <Badge variant="outline" className={`text-xs ${(settings?.diamond_points || 0) >= 200 ? 'border-green-500 text-green-600' : ''}`}>
                    Min: 200 pts ($10)
                  </Badge>
                </div>
              </div>
              
              {/* Programs */}
              <div className={`p-3 rounded-lg border ${(settings?.diamond_points || 0) >= 200 ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">✨</span>
                    <div>
                      <p className="font-medium text-sm text-forest">Programs & Experiences</p>
                      <p className="text-xs text-gray-500">Inner Edge, Retreats, SDC</p>
                    </div>
                  </div>
                  <Badge variant="outline" className={`text-xs ${(settings?.diamond_points || 0) >= 200 ? 'border-green-500 text-green-600' : ''}`}>
                    Min: 200 pts ($10)
                  </Badge>
                </div>
              </div>
            </div>
            
            {/* Gift a Membership CTA */}
            <div className="p-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg border border-pink-200 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Gift className="h-5 w-5 text-pink-600" />
                <span className="font-medium text-pink-800">Gift the Sisterhood</span>
              </div>
              <p className="text-sm text-pink-700 mb-3">
                Share this community with a friend or family member! They get a free month, and you earn <strong>100 diamonds ($5)</strong> when they join.
              </p>
              <Button 
                onClick={() => window.location.href = '/gift-membership'}
                variant="outline"
                className="w-full border-pink-300 text-pink-700 hover:bg-pink-100"
                data-testid="gift-membership-cta"
              >
                <Heart className="h-4 w-4 mr-2" />
                Send a Gift Membership
              </Button>
            </div>

            <div className="pt-4 border-t">
              <p className="text-sm font-medium text-forest mb-2">How to earn diamonds:</p>
              <div className="text-sm text-gray-600 space-y-1">
                <p>• <strong>Refer a friend:</strong> 100 diamonds ($5) when they join</p>
                <p>• Daily login: 1 diamond</p>
                <p>• Comment on reflections: 5 diamonds</p>
                <p>• Journal entry: 10 diamonds</p>
                <p>• Create a circle: 15 diamonds</p>
              </div>
            </div>
            
            {/* Milestones */}
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm font-medium text-forest mb-2">Activity Milestones:</p>
              <div className="text-sm text-gray-600 space-y-1">
                <p>• <strong>3 months active:</strong> Guaranteed $10 in swag rewards</p>
                <p>• <strong>6 months active:</strong> Guaranteed $25 toward coaching</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* My Journey Section */}
        <Card className="card-base mb-6" data-testid="my-journey-section">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Award className="h-5 w-5 text-terracotta" />
              My Sister Journey
            </CardTitle>
            <CardDescription>Celebrating your growth milestones</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingJourney ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-terracotta" />
              </div>
            ) : journeyStatus ? (
              <div className="space-y-6">
                {/* Journey Stats */}
                <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-sand/30 to-blush/30 rounded-lg">
                  <div className="flex-shrink-0">
                    <div className="h-16 w-16 rounded-full bg-terracotta/10 flex items-center justify-center">
                      <Calendar className="h-8 w-8 text-terracotta" />
                    </div>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-forest">
                      {journeyStatus.days_as_member} days
                    </p>
                    <p className="text-sm text-gray-600">as a {journeyStatus.membership_tier?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Sister'}</p>
                  </div>
                </div>

                {/* Milestones Achieved */}
                <div>
                  <p className="text-sm font-medium text-forest mb-3">Milestones Celebrated:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {['3_month', '6_month', '9_month', '12_month'].map((milestone) => {
                      const achieved = journeyStatus.prompts_received?.includes(milestone);
                      const labels = {
                        '3_month': '3 Months',
                        '6_month': '6 Months',
                        '9_month': '9 Months',
                        '12_month': '1 Year'
                      };
                      return (
                        <div
                          key={milestone}
                          className={`p-3 rounded-lg border flex items-center gap-2 ${
                            achieved 
                              ? 'bg-green-50 border-green-200' 
                              : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          {achieved ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <div className="h-4 w-4 rounded-full border-2 border-gray-300" />
                          )}
                          <span className={`text-sm ${achieved ? 'text-green-700 font-medium' : 'text-gray-500'}`}>
                            {labels[milestone]}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Next Milestone */}
                {journeyStatus.next_milestone && (
                  <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="h-4 w-4 text-purple-600" />
                      <p className="text-sm font-medium text-purple-800">Coming Up Next</p>
                    </div>
                    <p className="text-gray-700">
                      Your <span className="font-semibold text-purple-700">{journeyStatus.next_milestone.description}</span> is in{' '}
                      <span className="font-bold text-purple-600">{journeyStatus.next_milestone.days_until || journeyStatus.next_milestone.days_remaining} days</span>!
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      You'll receive a special message celebrating your journey.
                    </p>
                  </div>
                )}

                {/* Inner Edge Renewals (if applicable) */}
                {journeyStatus.membership_tier === 'inner_edge' && journeyStatus.renewal_prompts_received?.length > 0 && (
                  <div className="p-4 bg-terracotta/10 rounded-lg">
                    <p className="text-sm text-forest">
                      <strong>Inner Edge Status:</strong> You've been reminded about your renewal options. 
                      Reach out to us if you have questions about continuing your journey!
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">Unable to load journey information</p>
            )}
          </CardContent>
        </Card>

        {/* Profile Reflections Section - Enhanced Profile */}
        <Card className="card-base mb-6">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-terracotta" />
              Soul Reflections
            </CardTitle>
            <CardDescription>Share what is rising in your heart with the sisterhood</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">
              These reflections help your sisters connect with you and understand your journey. 
              Earn 5 diamonds for each reflection you pour out!
            </p>
            
            {reflectionPrompts.map((prompt, index) => (
              <div key={prompt.id} className="p-4 rounded-lg bg-sand/30 border border-sand">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <p className="font-medium text-forest">{prompt.prompt}</p>
                  {prompt.response && !editingReflection && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => startEditingReflection(prompt)}
                      className="text-gray-500 hover:text-forest"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                
                {editingReflection === prompt.id ? (
                  <div className="space-y-3">
                    <Textarea
                      value={reflectionText}
                      onChange={(e) => setReflectionText(e.target.value)}
                      placeholder="Share your thoughts..."
                      className="input-base min-h-[80px]"
                    />
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => handleSaveReflection(prompt.id)}
                        disabled={savingReflection}
                        className="bg-terracotta hover:bg-terracotta/90 text-white"
                      >
                        {savingReflection ? 'Saving...' : 'Save'}
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          setEditingReflection(null);
                          setReflectionText('');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : prompt.response ? (
                  <p className="text-gray-600 text-sm italic">&ldquo;{prompt.response}&rdquo;</p>
                ) : (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => startEditingReflection(prompt)}
                    className="text-terracotta border-terracotta/30 hover:bg-terracotta/10"
                  >
                    Add your reflection
                  </Button>
                )}
                
                {prompt.response && (
                  <div className="flex items-center gap-1 mt-2 text-green-600 text-xs">
                    <Check className="h-3 w-3" /> Completed
                  </div>
                )}
              </div>
            ))}
            
            {reflectionPrompts.length === 0 && (
              <p className="text-gray-500 text-center py-4">Loading reflection prompts...</p>
            )}
          </CardContent>
        </Card>

        {/* Notifications Section */}
        <Card className="card-base mb-6" data-testid="notification-preferences-section">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Bell className="h-5 w-5 text-forest" />
              Stay in the Circle
            </CardTitle>
            <CardDescription>Choose how we reach you with soul food and sisterhood moments</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Delivery Method Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium text-forest">How would you like to receive notifications?</Label>
              <RadioGroup 
                value={notificationPrefs.delivery_method} 
                onValueChange={(value) => setNotificationPrefs(prev => ({ ...prev, delivery_method: value }))}
                className="grid grid-cols-3 gap-3"
                data-testid="delivery-method-group"
              >
                <div>
                  <RadioGroupItem value="email" id="pref-email" className="peer sr-only" />
                  <Label
                    htmlFor="pref-email"
                    className="flex flex-col items-center justify-center rounded-lg border-2 border-gray-200 p-4 hover:bg-sand/30 peer-data-[state=checked]:border-terracotta peer-data-[state=checked]:bg-terracotta/10 cursor-pointer transition-all"
                  >
                    <Mail className="h-6 w-6 mb-2 text-forest" />
                    <span className="text-sm font-medium">Email Only</span>
                  </Label>
                </div>
                <div>
                  <RadioGroupItem value="sms" id="pref-sms" className="peer sr-only" disabled={!notificationPrefs.sms_available} />
                  <Label
                    htmlFor="pref-sms"
                    className={`flex flex-col items-center justify-center rounded-lg border-2 border-gray-200 p-4 hover:bg-sand/30 peer-data-[state=checked]:border-terracotta peer-data-[state=checked]:bg-terracotta/10 cursor-pointer transition-all ${!notificationPrefs.sms_available ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <MessageSquare className="h-6 w-6 mb-2 text-forest" />
                    <span className="text-sm font-medium">Text Only</span>
                    {!notificationPrefs.sms_available && <span className="text-xs text-gray-400">Coming Soon</span>}
                  </Label>
                </div>
                <div>
                  <RadioGroupItem value="both" id="pref-both" className="peer sr-only" disabled={!notificationPrefs.sms_available} />
                  <Label
                    htmlFor="pref-both"
                    className={`flex flex-col items-center justify-center rounded-lg border-2 border-gray-200 p-4 hover:bg-sand/30 peer-data-[state=checked]:border-terracotta peer-data-[state=checked]:bg-terracotta/10 cursor-pointer transition-all ${!notificationPrefs.sms_available ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex gap-1 mb-2">
                      <Mail className="h-5 w-5 text-forest" />
                      <MessageSquare className="h-5 w-5 text-forest" />
                    </div>
                    <span className="text-sm font-medium">Both</span>
                    {!notificationPrefs.sms_available && <span className="text-xs text-gray-400">Coming Soon</span>}
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Phone Number - Show if SMS selected */}
            {(notificationPrefs.delivery_method === 'sms' || notificationPrefs.delivery_method === 'both') && (
              <div className="space-y-3 p-4 bg-sand/20 rounded-lg">
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Phone Number
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={notificationPrefs.phone_number || ''}
                    onChange={(e) => setNotificationPrefs(prev => ({ ...prev, phone_number: e.target.value }))}
                    className="input-base flex-1"
                    data-testid="phone-number-input"
                  />
                  {notificationPrefs.phone_number && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={sendTestSms}
                      disabled={sendingTestSms}
                      className="whitespace-nowrap"
                    >
                      {sendingTestSms ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send Test'}
                    </Button>
                  )}
                </div>
                <p className="text-xs text-gray-500">We&apos;ll use this number for session reminders and important updates</p>
              </div>
            )}

            <Separator />

            {/* Notification Categories */}
            <div className="space-y-4">
              <Label className="text-sm font-medium text-forest">What do you want to hear about?</Label>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-forest">Session Reminders</p>
                  <p className="text-sm text-gray-500">Upcoming coaching sessions and gatherings</p>
                </div>
                <Switch 
                  checked={notificationPrefs.categories?.session_reminder ?? true}
                  onCheckedChange={(checked) => setNotificationPrefs(prev => ({ 
                    ...prev, 
                    categories: { ...prev.categories, session_reminder: checked }
                  }))}
                  data-testid="notification-session-switch"
                />
              </div>
              
              <Separator className="my-2" />
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-forest">Sister Messages</p>
                  <p className="text-sm text-gray-500">When a sister reaches out to connect</p>
                </div>
                <Switch 
                  checked={notificationPrefs.categories?.new_message ?? true}
                  onCheckedChange={(checked) => setNotificationPrefs(prev => ({ 
                    ...prev, 
                    categories: { ...prev.categories, new_message: checked }
                  }))}
                  data-testid="notification-messages-switch"
                />
              </div>
              
              <Separator className="my-2" />
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-forest">Circle Updates</p>
                  <p className="text-sm text-gray-500">Replies and reactions to your posts</p>
                </div>
                <Switch 
                  checked={notificationPrefs.categories?.community_activity ?? true}
                  onCheckedChange={(checked) => setNotificationPrefs(prev => ({ 
                    ...prev, 
                    categories: { ...prev.categories, community_activity: checked }
                  }))}
                  data-testid="notification-community-switch"
                />
              </div>
              
              <Separator className="my-2" />
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-forest">Journey Milestones</p>
                  <p className="text-sm text-gray-500">Celebrating your growth at 3, 6, 9, 12 months</p>
                </div>
                <Switch 
                  checked={notificationPrefs.categories?.journey_milestone ?? true}
                  onCheckedChange={(checked) => setNotificationPrefs(prev => ({ 
                    ...prev, 
                    categories: { ...prev.categories, journey_milestone: checked }
                  }))}
                  data-testid="notification-journey-switch"
                />
              </div>
            </div>

            {/* Save Button */}
            <Button 
              onClick={saveNotificationPrefs}
              disabled={savingNotificationPrefs}
              className="w-full btn-primary"
            >
              {savingNotificationPrefs ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Save Notification Preferences'
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Weekly Circle Digest Section */}
        <Card className="card-base mb-6">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Mail className="h-5 w-5 text-forest" />
              Weekly Circle Digest
            </CardTitle>
            <CardDescription>Get a summary of new circles and popular posts from your community</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              Every week, we send you a digest with new circles matching your interests and the most popular posts from circles you have joined.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                variant="outline" 
                className="btn-outline"
                onClick={async () => {
                  setLoadingDigest(true);
                  try {
                    const res = await axios.get(`${API_URL}/api/user/digest/preview`);
                    setDigestPreview(res.data);
                    toast.success('Digest preview loaded!');
                  } catch (error) {
                    toast.error('Failed to load digest preview');
                  } finally {
                    setLoadingDigest(false);
                  }
                }}
                disabled={loadingDigest}
              >
                {loadingDigest ? 'Loading...' : 'Preview My Digest'}
              </Button>
              
              <Button 
                className="btn-primary"
                onClick={async () => {
                  setSendingDigest(true);
                  try {
                    const res = await axios.post(`${API_URL}/api/user/digest/send-now`);
                    if (res.data.sent) {
                      toast.success('Digest sent to your email!');
                      quickCelebrate('sparkle');
                    } else {
                      toast.info(res.data.message);
                    }
                  } catch (error) {
                    toast.error('Failed to send digest');
                  } finally {
                    setSendingDigest(false);
                  }
                }}
                disabled={sendingDigest}
              >
                <Send className="h-4 w-4 mr-2" />
                {sendingDigest ? 'Sending...' : 'Send Digest Now'}
              </Button>
            </div>
            
            {digestPreview && (
              <div className="mt-4 p-4 bg-sand/30 rounded-lg">
                <h4 className="font-medium text-forest mb-3">Your Digest Preview</h4>
                
                {digestPreview.new_circles?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-terracotta mb-2">✨ New Circles For You ({digestPreview.new_circles.length})</p>
                    {digestPreview.new_circles.slice(0, 3).map((circle, idx) => (
                      <div key={idx} className="text-sm text-gray-600 ml-4 mb-1">
                        • {circle.name}
                      </div>
                    ))}
                  </div>
                )}
                
                {digestPreview.popular_posts?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-terracotta mb-2">🔥 Popular Posts ({digestPreview.popular_posts.length})</p>
                    {digestPreview.popular_posts.slice(0, 3).map((post, idx) => (
                      <div key={idx} className="text-sm text-gray-600 ml-4 mb-1">
                        • <span className="font-medium">{post.circle_name}:</span> {post.content_preview}
                      </div>
                    ))}
                  </div>
                )}
                
                {!digestPreview.new_circles?.length && !digestPreview.popular_posts?.length && (
                  <p className="text-sm text-gray-500">No digest content available this week. Join more circles to get personalized updates!</p>
                )}
                
                <p className="text-xs text-gray-400 mt-2">You are in {digestPreview.joined_circle_count || 0} circle(s)</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Location Section */}
        <Card className="card-base mb-6">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <MapPin className="h-5 w-5 text-forest" />
              Find Your Sisters Nearby
            </CardTitle>
            <CardDescription>Connect with sisters in your area</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-forest">Share Your Location</p>
                <p className="text-sm text-gray-500">
                  Help nearby sisters find you. Only approximate distance is shared.
                </p>
              </div>
              <Switch 
                checked={locationEnabled}
                onCheckedChange={handleToggleLocation}
                data-testid="location-switch"
              />
            </div>
            {user?.location && (
              <p className="text-xs text-gray-500 mt-4">
                Location last updated. Approximate coordinates stored.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Security Section */}
        <Card className="card-base mb-6">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Shield className="h-5 w-5 text-forest" />
              Protect Your Space
            </CardTitle>
            <CardDescription>Keep your sanctuary secure</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="currentPassword">Current Password</Label>
              <Input 
                id="currentPassword" 
                type="password" 
                className="input-base mt-1"
              />
            </div>
            <div>
              <Label htmlFor="newPassword">New Password</Label>
              <Input 
                id="newPassword" 
                type="password" 
                className="input-base mt-1"
              />
            </div>
            <div>
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input 
                id="confirmPassword" 
                type="password" 
                className="input-base mt-1"
              />
            </div>
            <Button variant="outline" className="btn-outline">
              Update Password
            </Button>
          </CardContent>
        </Card>

        {/* Membership Management Section */}
        {(hasInnerCircle || hasSDC || hasInnerEdge || hasRetreat) && (
          <Card className="card-base mb-6">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-forest" />
                Membership Management
              </CardTitle>
              <CardDescription>View and manage your active memberships</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Active Memberships List */}
              <div className="space-y-3">
                {hasInnerCircle && (
                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl border border-purple-200">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <Crown className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-forest">Inner Circle</p>
                        <p className="text-sm text-gray-500">Monthly recurring membership</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                      onClick={() => setShowCancelDialog(true)}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
                
                {hasInnerEdge && (
                  <div className="flex items-center justify-between p-4 bg-amber-50 rounded-xl border border-amber-200">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                        <Sparkles className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-forest">Inner Edge</p>
                        <p className="text-sm text-gray-500">Coaching program</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-amber-600 border-amber-300">Non-refundable</Badge>
                  </div>
                )}
                
                {hasSDC && (
                  <div className="flex items-center justify-between p-4 bg-teal-50 rounded-xl border border-teal-200">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center">
                        <Users className="h-5 w-5 text-teal-600" />
                      </div>
                      <div>
                        <p className="font-medium text-forest">SDC Lounge</p>
                        <p className="text-sm text-gray-500">Executive community</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-teal-600 border-teal-300">Non-refundable</Badge>
                  </div>
                )}
                
                {hasRetreat && (
                  <div className="flex items-center justify-between p-4 bg-terracotta/10 rounded-xl border border-terracotta/30">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-terracotta/20 flex items-center justify-center">
                        <Sparkles className="h-5 w-5 text-terracotta" />
                      </div>
                      <div>
                        <p className="font-medium text-forest">Costa Rica Retreat</p>
                        <p className="text-sm text-gray-500">Just Be: Soft Power™</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-terracotta border-terracotta/30">Non-refundable</Badge>
                  </div>
                )}
              </div>
              
              {/* Cancellation Policy Info */}
              <div className="mt-4 p-4 bg-gray-50 rounded-lg text-sm">
                <p className="font-medium text-forest mb-2">Cancellation Policy</p>
                <ul className="space-y-1 text-gray-600">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-500 mt-0.5" />
                    <span><strong>Inner Circle:</strong> Cancel anytime. Access continues until end of billing period.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="h-4 w-4 text-red-500 mt-0.5" />
                    <span><strong>Inner Edge, SDC, & Retreat:</strong> Non-refundable. Contact admin for questions.</span>
                  </li>
                </ul>
                <p className="mt-3 text-xs text-gray-500">
                  Questions about your membership? <a href="mailto:support@sisterdiamonds.com" className="text-terracotta hover:underline">Contact our support team</a>
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Logout */}
        <Card className="card-base border-red-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-red-600">Sign Out</h3>
                <p className="text-sm text-gray-500">Sign out of your account on this device</p>
              </div>
              <Button 
                variant="outline" 
                className="border-red-300 text-red-600 hover:bg-red-50"
                onClick={logout}
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Inner Circle Cancellation Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Cancel Inner Circle Membership
            </DialogTitle>
            <DialogDescription>
              We&apos;re sad to see you go, Sister. Your access will continue until the end of your current billing period.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>What you&apos;ll lose:</strong>
              </p>
              <ul className="mt-2 space-y-1 text-sm text-amber-700">
                <li>• Access to Inner Circle communities</li>
                <li>• Monthly group coaching sessions</li>
                <li>• Sister Diamond events & retreats discounts</li>
                <li>• Premium content library</li>
              </ul>
            </div>
            
            <div>
              <Label htmlFor="cancel-reason" className="text-sm">
                Help us improve: Why are you cancelling? (Optional)
              </Label>
              <Textarea
                id="cancel-reason"
                placeholder="Share your feedback..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="mt-2"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => setShowCancelDialog(false)}
              className="flex-1"
            >
              Keep My Membership
            </Button>
            <Button 
              variant="destructive"
              onClick={handleCancelMembership}
              disabled={cancellingMembership}
              className="flex-1"
            >
              {cancellingMembership ? 'Cancelling...' : 'Confirm Cancellation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancellation Success Dialog */}
      <Dialog open={showCancelSuccessDialog} onOpenChange={setShowCancelSuccessDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-forest text-center">
              Membership Cancelled
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-6 text-center space-y-4">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto">
              <Crown className="h-10 w-10 text-purple-400" />
            </div>
            
            <div className="bg-forest/5 rounded-xl p-4">
              <p className="text-3xl font-bold text-forest">
                {cancellationResult?.daysRemaining || 30} days
              </p>
              <p className="text-gray-600 mt-1">remaining in your membership</p>
            </div>
            
            <p className="text-gray-600">
              You still have access to all Inner Circle features until your membership ends. 
              We&apos;d love to have you stay, Sister.
            </p>
            
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <p className="text-sm text-amber-800 font-medium">
                Changed your mind?
              </p>
              <p className="text-sm text-amber-700 mt-1">
                You can reactivate your membership anytime before it expires.
              </p>
            </div>
          </div>
          
          <DialogFooter className="flex flex-col gap-3">
            <Button 
              onClick={handleReactivateMembership}
              className="w-full bg-terracotta hover:bg-terracotta/90 text-white"
            >
              <Heart className="h-4 w-4 mr-2" />
              I Want to Stay!
            </Button>
            <Button 
              variant="ghost"
              onClick={() => {
                setShowCancelSuccessDialog(false);
                window.location.reload();
              }}
              className="w-full text-gray-500"
            >
              Continue to Dashboard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default SettingsPage;
